/**
 * IMPORTANT: All positioning is interpreted as a 1-based index
 * 1 2 3
 * 4 5 6
 * 7 8 9
 *
 * Row 1, Column 1 = Position 1
 * Row 1, Column 2 = Position 2
 * Row 1, Column 3 = Position 3
 * Row 2, Column 1 = Position 4
 * Row 2, Column 2 = Position 5
 * Row 2, Column 3 = Position 6
 * */

import { GRID_CARD_TYPES } from '../constants';

/**
 * Get the number of cells wide for the asset. We need to translate this to a usable number
 * for the asset placement algo.
 * TODO: is 'full' case sensitive?
 *
 * @param {Number | String} cellsWide String can be 'full' or a number
 * @param {Number} numGridCols Number of grid columns
 * @returns {Number} Number of cells wide
 */
const getEffectiveCellsWide = (cellsWide, numGridCols) => {
  if (cellsWide === 'full') {
    return numGridCols;
  }

  if (typeof cellsWide === 'number') {
    return cellsWide;
  }

  return 1;
};

/**
 * Get the effective column for the asset. We need to translate this to a usable number
 * for the asset placement algo.
 * TODO: are 'left' and 'right' case sensitive?
 *
 * @param {Number | String} column String can be 'left' or 'right'
 * @param {Number} numGridCols Number of grid columns
 * @param {Number} cellsWide Number of cells wide
 * @returns {Number} Effective column
 */
const getEffectiveColumn = (column, numGridCols, cellsWide) => {
  if (column === 'left') {
    return 1;
  }

  // The column needs to represent the first cell index if the asset spans multiple cells
  if (column === 'right') {
    return numGridCols - cellsWide + 1;
  }

  // If the column is 'full', we need to return 1, as the asset will span the entire row
  // This will override the column value
  if (cellsWide === 'full') {
    return 1;
  }

  if (typeof column === 'number') {
    return column;
  }

  return 1;
};

/**
 * We're currently supporting 2 implementations of the grid:
 * 1. row, column, cellsTall, cellsWide (ideal)
 * 2. position, size (legacy)
 *
 * We need to translate the legacy implementation to the new implementation
 * until all assets are migrated to the new implementation CMS. There are also no user validations
 * from the CMS side, so we need to handle all fallbacks and edge cases here.
 * @param {Object} asset The asset object
 * @param {Number} numGridCols The number of grid columns
 * @returns {Object} The effective asset position
 */
const getEffectiveAssetPosition = ({ asset, numGridCols }) => {
  const customVars = asset?.options?.customVars;
  if (!customVars || !numGridCols) {
    return {};
  }

  const {
    position,
    size,
    row,
    column,
    cellsTall,
    cellsWide,
    minColumns,
    maxColumns,
  } = customVars;

  // Translate legacy `position` and `size` fields to new `row`, `column`, `cellsTall`, `cellsWide`
  // `position` -> `row`, `column` based on number of grid cols
  // `size` -> `cellsWide`, assume cellsTall = 1
  // TODO: remove this once all assets are migrated to the new implementation
  if (position) {
    return {
      row: Math.ceil(position / numGridCols),
      column: ((position - 1) % numGridCols) + 1,
      cellsWide: size || 1,
      cellsTall: 1,
      minColumns,
      maxColumns,
      priority: asset?.priority,
    };
  }

  // TODO: prioritize the new implementation over the legacy implementation
  return {
    row,
    column: getEffectiveColumn(column, numGridCols, cellsWide),
    cellsWide: getEffectiveCellsWide(cellsWide, numGridCols),
    cellsTall: cellsTall || 1,
    minColumns,
    maxColumns,
    priority: asset?.priority,
  };
};

/**
 * Sort the assets by row, column, and priority
 * 1. Row
 * 2. Column
 * 3. Priority
 *
 * TODO: check if this is correct
 * @param {Object} a The first asset
 * @param {Object} b The second asset
 * @returns {Number} The comparison result
 */
const sortAssetsByRowColAndPriority = (a, b) => {
  const aRow = a?.effectiveCustomVars?.row || 0;
  const aCol = a?.effectiveCustomVars?.column || 0;
  const aPriority = a?.effectiveCustomVars?.priority || 0;
  const bRow = b?.effectiveCustomVars?.row || 0;
  const bCol = b?.effectiveCustomVars?.column || 0;
  const bPriority = b?.effectiveCustomVars?.priority || 0;
  if (aRow !== bRow) {
    return aRow - bRow;
  }
  if (aCol !== bCol) {
    return aCol - bCol;
  }
  return aPriority - bPriority;
};

/**
 * Check if the asset is eligible to be injected into the grid by grid column constraints
 * 1. Check if the asset is within the min and max columns
 * 2. Check if the asset can fit in the grid
 *
 * @param {minColumns} minColumns The minimum number of columns the grid must have for this asset to show
 * @param {maxColumns} maxColumns The maximum number of columns the grid must have for this asset to show
 * @param {cellsWide} cellsWide The number of cells wide the asset spans
 * @param {numGridCols} numGridCols The number of grid columns
 * @param {column} column The column number the asset is in (1-based index, where the index is the top-left most cell for a multi-cell asset)
 * @returns {Boolean} True if the asset is eligible, false otherwise
 */
const isAssetEligibleByGridCol = ({
  minColumns,
  maxColumns,
  cellsWide,
  numGridCols = 1,
  column,
}) => {
  // User defined min and max columns, usually to handle showing/hiding assets based on screen size
  // Ignore if not defined. Empty string will be coerced to 0 by the isNaN check so we need the boolean check
  if (
    (Boolean(minColumns) && !isNaN(minColumns) && numGridCols < minColumns) ||
    (Boolean(maxColumns) && !isNaN(maxColumns) && numGridCols > maxColumns)
  ) {
    return false;
  }

  // Check if asset has a valid column and cellsWide for calculation
  if (isNaN(column) || column < 1 || isNaN(cellsWide) || cellsWide < 1) {
    return false;
  }

  // Check if the total asset span can fit in the grid
  if (column - 1 + cellsWide > numGridCols) {
    return false;
  }

  return true;
};

/**
 * Create a map of the asset cell reservation
 * 1. Reserve cells in the grid for each asset
 * 2. Check if the asset can fit in the grid
 * 3. Check if the asset overlaps with other assets
 * 4. If the asset overlaps with other assets, skip it
 * 5. If the asset can fit in the grid, add it to the map
 * 6. Return the map
 *
 * Note: The assets should have already been pre-filtered by grid column eligibility
 * and sorted by row, column, and priority. We only check for overlapping assets
 * here, as the other checks have already been done.
 *
 * @param {Array} assets An array of assets
 * @param {Number} numGridCols The number of grid columns
 * @returns {Map} A map of the asset cell reservation, where the key is the `{row}-{column}` index and the value is the asset id
 *
 * @example return
 * {
 *   "1-1": 123,
 *   "1-2": 123,
 *   "1-3": 123,
 *   "2-2": 456,
 *   "6-2": 789,
 *   "7-2": 789,
 * }
 *
 * where
 * - asset 123 is in row 1, column 1-3 (single row, 3 columns wide)
 * - asset 456 is in row 2, column 2 (single row, 1 column wide)
 * - asset 789 is in row 6-7, column 2 (2 rows, 1 column wide)
 */
const getAssetReservationMap = ({ assets, numGridCols }) => {
  const assetCellReservationMap = new Map();

  if (!assets?.length || !numGridCols) {
    return assetCellReservationMap;
  }

  // Check if there exists any reserved cells in the grid for the asset
  const checkIfAlreadyReserved = ({ row, column, cellsWide, cellsTall }) => {
    for (let i = row; i < row + cellsTall; i++) {
      for (let j = column; j < column + cellsWide; j++) {
        if (assetCellReservationMap.has(`${i}-${j}`)) {
          return true;
        }
      }
    }
    return false;
  };

  // Reserve all cells in the grid needed for the asset
  const reserveAssetInGrid = ({ id, row, column, cellsWide, cellsTall }) => {
    for (let i = row; i < row + cellsTall; i++) {
      for (let j = column; j < column + cellsWide; j++) {
        if (!assetCellReservationMap.has(`${i}-${j}`)) {
          assetCellReservationMap.set(`${i}-${j}`, id);
        } else {
          return false;
        }
      }
    }
  };

  const attemptToReserveAssetInGrid = (asset) => {
    const { row, column, cellsWide, cellsTall } =
      asset?.effectiveCustomVars || {};

    // All cells must be open for the asset to be placed. If any cell is already reserved, skip the asset
    if (!checkIfAlreadyReserved({ row, column, cellsWide, cellsTall })) {
      reserveAssetInGrid({ row, column, cellsWide, cellsTall, id: asset?.id });
      return asset;
    }

    return null;
  };

  assets.forEach((asset) => {
    attemptToReserveAssetInGrid(asset);
  });

  return assetCellReservationMap;
};

/**
 * Get the remaining assets that are not overlapping with other assets
 * based on the asset cell reservation map. Filter out any assets that
 * don't have a cell reservation in the grid (i.e. overlapping assets).
 *
 * @param {Array} assets An array of assets
 * @param {Map} assetCellReservationMap A map of the asset cell reservation
 * @returns {Array} An array of non-overlapping assets
 */
const getNonOverlappingAssets = ({ assets, assetCellReservationMap }) => {
  if (!assets?.length || !assetCellReservationMap) {
    return [];
  }

  const nonOverlappingAssets = assets.filter((asset) => {
    const { row, column } = asset?.effectiveCustomVars || {};
    return assetCellReservationMap.get(`${row}-${column}`);
  });

  return nonOverlappingAssets;
};

/**
 * Asset Eligibilty Criteria (for placement in the grid):
 * 1. Assets will take priority by row, then column, then assetPriority (lower number = higher priority)
 * 2. Assets must fit within the grid column range defined by the user within the CMS
 * 3. Assets must not overlap with other assets. Reserve cells in the grid for each asset
 *    and remove any overlapping assets (determined by asset priority from criteria 1)
 *
 * @param {Array} assets An array of assets
 * @param {Number} numGridCols The number of grid columns
 * @returns {Object} ret An object containing the asset cell reservation map and the eligible assets
 * @returns {Array} ret.eligibleAssets An array of eligible assets
 * @returns {Map} ret.assetCellReservationMap A map of the asset cell reservation
 */
export const getEligibleAssets = ({
  assets = [],
  numGridCols,
  ignoreMultiRowAndMultiColumnAssets = false,
}) => {
  if (!assets?.length || !numGridCols) {
    return {
      assetCellReservationMap: new Map(),
      eligibleAssets: [],
    };
  }

  // Get the effective custom variables for each asset so that we can perform asset placement calculations
  const assetsWithEffectiveCustomVars = assets.map((asset) => ({
    ...asset,
    effectiveCustomVars: getEffectiveAssetPosition({ asset, numGridCols }),
  }));

  // Sort the assets by row, column, and asset priority
  const sortedAssets = assetsWithEffectiveCustomVars.toSorted(
    sortAssetsByRowColAndPriority
  );

  // Filter the assets by grid column eligibility
  const filteredAssets = sortedAssets.filter((asset) => {
    const { cellsWide, cellsTall, minColumns, maxColumns, column } =
      asset?.effectiveCustomVars || {};

    if (
      ignoreMultiRowAndMultiColumnAssets &&
      (cellsWide > 1 || cellsTall > 1)
    ) {
      return false;
    }

    const isEligible = isAssetEligibleByGridCol({
      minColumns,
      maxColumns,
      cellsWide,
      numGridCols,
      column,
    });

    return isEligible;
  });

  // Create a virtual grid to reserve cells for each asset and skip overlapping assets
  const assetCellReservationMap = getAssetReservationMap({
    assets: filteredAssets,
    numGridCols,
  });

  // Filter out overlapping assets based on the asset cell reservation map
  const nonOverlappingAssets = getNonOverlappingAssets({
    assets: filteredAssets,
    assetCellReservationMap,
  });

  return {
    assetCellReservationMap,
    eligibleAssets: nonOverlappingAssets,
  };
};

/**
 * Merge the products and assets into a single grid. Algo overview:
 * virtualList = [];
 * while we have products left to place:
 *   virtualListItem = []; // each virtual list item will represent a virtual grid that can span multiple rows and columns
 *   virtualListItemRowSpan = 1;
 *   virtualListItemColumnSpan = numGridCols;
 *   for each virtual grid row:
 *     for each virtual grid column:
 *       if cell is not reserved for an asset:
 *         place product in cell
 *       if asset is not top left corner of the asset index:
 *         skip to the next cell
 *       all conditions are met, place the asset in the virtual grid
 *       if the asset will overflow the virtual grid
 *         adjust the virtualListItemRowSpan to accommodate the asset
 *     if we are at the last row of the virtual grid and there are no products left
 *       if there are any empty cells in between marketing assets
 *          remove all multi-row spanning marketing assets from the virtual grid
 *    add the virtualListItem (virtual grid) to the virtualList
 *
 * Visual Representation of the Virtual List:
 * --------------
 * 1 2 3      Virtual List Item 1: 1x3 Virtual Grid
 * --------------
 * 4 A A
 * 5 B 6      Virtual List Item 2: 3x3 Virtual Grid
 * 7 B 8
 * --------------
 *  9 C C     Virtual List Item 3: 2x3 Virtual Grid
 * 10 C C
 *
 * where numbers are products and letters are assets
 *
 * Note: We "remove all multi-row spanning marketing assets from the virtual grid"
 * as the easiest way to handle the edge case of having empty cells in between marketing assets.
 * We can attempt to remove assets one at a time and reogranize the virtual grid, but this is
 * a more complex solution and not worth the effort at this time. These sitations are rare, especially
 * as the business tends to use single row assets in the grid.
 *
 * @param {Array} products An array of products
 * @param {Number} numGridCols The number of grid columns
 * @param {Array} eligibleAssets An array of eligible assets that can be placed in the grid
 * @param {Map} assetCellReservationMap A map of the asset cell reservation
 * @returns {Array} A virtual list of products and assets
 *
 * @example return
 * [
 *   [
 *     { id: 123, type: 'product' },
 *     { id: 456, type: 'product' },
 *     { id: 789, type: 'product' },
 *     { id: 101, type: 'asset' },
 *     { id: 102, type: 'asset' },
 *   ],
 *   [
 *     { id: 103, type: 'product' },
 *     { id: 104, type: 'product' },
 *     { id: 105, type: 'product' },
 *     { id: 106, type: 'asset' },
 *     { id: 107, type: 'asset' },
 *   ],
 *   [
 *     { id: 108, type: 'product' },
 *     { id: 109, type: 'product' },
 *     { id: 110, type: 'product' },
 *     { id: 111, type: 'asset' },
 *     { id: 112, type: 'asset' },
 *   ],
 * ]
 **/
export const getMergedProductsAndAssets = ({
  products,
  numGridCols,
  eligibleAssets,
  assetCellReservationMap = new Map(),
}) => {
  // For edge case testing only, check product length of 3,4,5,6 in conjunction with mockInGridAssets
  // products = products.slice(0, 6); // for testing, uncomment this line

  if (!products?.length || !numGridCols) {
    return [];
  }

  const virtualList = [];
  let currVirtualListIndex = 1;
  let productIndex = 0;

  // Keep building the virtual list until all products are placed
  while (productIndex < products.length) {
    // each virtual list item will represent a virtual grid that can span multiple rows and columns
    // but it will be stored as a single row in the virtual list.
    // this is because react-virtual indexes based of rows and we need to support assets that span multiple rows
    let virtualListItem = [];
    const virtualListItemColumnSpan = numGridCols; // columnns will never change
    let virtualListItemRowSpan = 1; // number of will change based on the asset size

    // Build the virtual grid for the current virtual list item
    // Iterate over the virtual grid rows
    for (
      let currVirtualGridRowIndex = 1;
      currVirtualGridRowIndex <= virtualListItemRowSpan;
      currVirtualGridRowIndex++
    ) {
      // Iterate over the virtual grid columns
      for (
        let currVirtualGridColumnIndex = 1;
        currVirtualGridColumnIndex <= virtualListItemColumnSpan;
        currVirtualGridColumnIndex++
      ) {
        const virtualGridKey = `${currVirtualListIndex}-${currVirtualGridColumnIndex}`; // virtual grid is 1-based
        const reservedAssetId = assetCellReservationMap.get(virtualGridKey);
        const reservedAsset = eligibleAssets?.find(
          (asset) => asset.id === reservedAssetId
        );

        // If there are no assets reserved for this cell, attempt to place a product
        if (!reservedAsset) {
          // if there are no products left, break out of the loop
          // don't allow anymore assets to be placed in the remaining cells of this virtual grid row
          if (productIndex >= products.length) {
            break;
          }

          const product = products[productIndex];
          virtualListItem.push({
            ...product,
            type: GRID_CARD_TYPES.PRODUCT,
            span: {
              rows: 1,
              columns: 1,
            },
          });
          productIndex++;
          continue;
        }

        // We only want to place the asset if we are looking at the top left corner of the asset index for multi-cell assets
        const { row, column } = reservedAsset?.effectiveCustomVars || {};
        const isTopLeftCornerIndex =
          currVirtualListIndex === row && currVirtualGridColumnIndex === column;

        if (!isTopLeftCornerIndex) {
          continue;
        }

        // All conditions are met, place the asset in the virtual grid
        // Inject span and type into the asset object, so that the GridCardRenderer can use it
        virtualListItem.push({
          ...reservedAsset,
          type: GRID_CARD_TYPES.ASSET,
          span: {
            rows: reservedAsset?.effectiveCustomVars?.cellsTall,
            columns: reservedAsset?.effectiveCustomVars?.cellsWide,
          },
        });

        // Check if the asset will overflow the virtual grid
        const numVirtualGridRowsNeeded =
          currVirtualGridRowIndex +
          reservedAsset?.effectiveCustomVars?.cellsTall -
          1;

        // If asset is taller than the current virtual grid (virtualListItem),
        // adjust the virtualListItemRowSpan to accommodate the asset
        if (numVirtualGridRowsNeeded > virtualListItemRowSpan) {
          virtualListItemRowSpan = numVirtualGridRowsNeeded;
        }
      }

      // if there are no products left, and we are at the last row of the virtual grid
      // check if there are any empty cells in between marketing assets
      // if there are, remove all multi-row spanning marketing assets from the virtual grid
      if (
        currVirtualGridRowIndex === virtualListItemRowSpan &&
        productIndex >= products.length
      ) {
        const numVirtualGridCellsNeededToBeFilled =
          virtualListItemRowSpan * virtualListItemColumnSpan;

        const numCellsOccupiedByProductsAndAssets = virtualListItem?.reduce(
          (acc, item) => {
            const itemSpanRows = item?.span?.rows || 1;
            const itemSpanColumns = item?.span?.columns || 1;
            return acc + itemSpanRows * itemSpanColumns;
          },
          0
        );

        const numOfEmptyCells =
          numVirtualGridCellsNeededToBeFilled -
          numCellsOccupiedByProductsAndAssets;

        if (numOfEmptyCells > 0) {
          virtualListItem = virtualListItem.filter((item) => {
            if (item?.type !== GRID_CARD_TYPES.ASSET) {
              return true;
            }
            return item?.span?.rows === 1;
          });
        }
      }

      currVirtualListIndex++;
    }

    // Add the virtual grid to the virtual list
    virtualList.push(virtualListItem);
  }

  return virtualList;
};

/**
 * Filters a list of in-grid assets against active assortment test exclusion rules.
 *
 * Each asset may declare comma-separated exclusion lists in its CMS custom vars:
 * - `assortmentTestFplExclusions` — FPL codes that should suppress this asset
 * - `assortmentTestTagExclusions` — tag IDs that should suppress this asset
 *
 * An asset is excluded if any of the active `excludeFplCodes` appears in the
 * asset's FPL exclusion list, OR any of the active `excludeTagIds` appears in
 * the asset's tag exclusion list.
 *
 * @param {Object}   params
 * @param {Array}    params.assets          - Full list of in-grid assets to filter.
 * @param {string[]} params.excludeFplCodes - Active FPL codes to exclude (from assortment test config).
 * @param {string[]} params.excludeTagIds   - Active tag IDs to exclude (from assortment test config).
 * @returns {Array} Assets that are not suppressed by any active assortment test rule.
 */
export const filterAssetsByAssortmentExclusions = ({
  assets,
  excludeFplCodes,
  excludeTagIds,
}) => {
  if (!excludeFplCodes?.length && !excludeTagIds?.length) {
    return assets;
  }

  return assets.filter((asset) => {
    const assetFplExclusions = (
      asset.options?.customVars?.assortmentTestFplExclusions || ''
    ).split(',');
    const assetTagExclusions = (
      asset.options?.customVars?.assortmentTestTagExclusions || ''
    ).split(',');

    return (
      !excludeFplCodes.some((code) => assetFplExclusions.includes(code)) &&
      !excludeTagIds.some((id) => assetTagExclusions.includes(id))
    );
  });
};
